<template>
  <div class="flex w-screen gap-6 px-6 pt-6">
    <div
      id="list-of-targets"
      class="relative flex-shrink-0 pb-32 overflow-y-auto border rounded-t bg-pure-white border-slate-400 "
      :style="{ height: 'calc(100vh - 15.5rem)', width: '19.5rem' }"
    >
      <div
        class="sticky top-0 z-10 flex flex-col gap-1 px-6 py-5 bg-pure-white"
      >
        <BlTextSubtitle1 text="List of targets" />
        <BlTextBody2 :text="`${selectedTargets.length} target(s)`" />
      </div>
      <div
        v-for="target in selectedTargets"
        :key="target.id"
        class="relative flex flex-col h-32 gap-1 px-6 py-4 border cursor-pointer border-slate-400"
        :class="{ 'bg-slate-300': reviewed.id === target.id }"
        @click="onSelectReview(target)"
      >
        <div
          class="absolute left-0 top-0 h-32 w-1.5"
          :style="{ backgroundColor: target.curriculum_color }"
        />
        <BlTextBody2
          class="truncate"
          :text="target.curriculum_name"
          :title="target.curriculum_name"
        />
        <BlTextSubtitle3
          class="truncate text-slate-1000"
          :text="target.name"
          :title="target.name"
        />
        <BlTextBody2
          class="whitespace-pre-line line-clamp-2"
          :text="target.description"
          :title="target.description"
        />
      </div>
    </div>
    <div
      id="review-target"
      class="w-full overflow-hidden bg-pure-white"
      :style="{ height: 'calc(100vh - 15.5rem)' }"
    >
      <div v-if="reviewLoading" class="grid h-full place-items-center">
        <div class="blum-loader"></div>
      </div>
      <TargetProperty
        v-else-if="reviewed.id"
        class="w-full m-0 rounded"
        :style="{ height: 'calc(100vh - 13.5rem)' }"
        :target="reviewed"
        :graphEntries="graphEntries"
        :entries="entries"
        readonly
      />
    </div>
    <div
      v-if="reviewLoading"
      class="grid flex-shrink-0 place-items-center"
      :style="{ height: 'calc(100vh - 15.5rem)', width: '19.5rem' }"
    >
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="reviewed.id"
      id="target-measurement"
      class="flex flex-col items-center flex-shrink-0 pb-16 overflow-x-hidden overflow-y-auto"
      :style="{ height: 'calc(100vh - 15.5rem)', width: '19.5rem' }"
    >
      <div :style="{ width: '21rem', transform: 'scale(0.9)' }">
        <MeasurementCard
          class="min-h-128"
          :style="{ height: '100%' }"
          :measurement="{
            target_id: reviewed.id,
            target: reviewed,
            is_fixed: false,
            is_dropped: false,
            type: measurementType,
            results: measurementResults
          }"
          sessionStatus="completed"
        />
      </div>
      <div
        v-if="reviewed.type.includes('Percentage') && reviewed.probing_enable"
        class="mb-4"
      >
        <div class="flex justify-between p-1 mx-auto rounded w-38 bg-slate-400">
          <button
            class="px-2 h-7.5 flex items-center border outline-none"
            :class="[
              !probing
                ? 'bg-pure-white border-slate-500 rounded'
                : 'border-slate-400'
            ]"
            @click="probing = false"
          >
            <BlTextSubtitle3 text="Regular" />
          </button>
          <button
            class="px-2 h-7.5 flex items-center border outline-none"
            :class="[
              probing
                ? 'bg-pure-white border-slate-500 rounded'
                : 'border-slate-400'
            ]"
            @click="probing = true"
          >
            <BlTextSubtitle3 text="Probing" />
          </button>
        </div>
      </div>
      <div class="text-center">
        <BlTextSubtitle3
          v-if="probing"
          class="text-slate-600"
          text="This card will be displayed when you opt to conduct probing during the session."
        />
        <BlTextSubtitle3
          v-else-if="reviewed.probing_enable"
          class="text-slate-600"
          text="Card preview for sessions without activating probing"
        />
        <BlTextSubtitle3
          v-else
          class="text-slate-600"
          text="Card preview during the session"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import TargetProperty from "../Property.vue";
import MeasurementCard from "../../../sessions/MeasurementCard.vue";

import BlTextBody2 from "../../../BlTextBody2.vue";
import BlTextSubtitle1 from "../../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";

export default {
  name: "ReviewTargetDatabank",
  components: {
    TargetProperty,
    MeasurementCard,
    BlTextBody2,
    BlTextSubtitle1,
    BlTextSubtitle3
  },
  props: ["selectedTargets", "targetTypes"],
  data() {
    return {
      reviewLoading: false,
      reviewed: { id: null },
      probing: false,
      graphEntries: { labels: [], datasets: [], recorded_by: [] },
      entries: []
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    measurementType() {
      if (!this.reviewed.id) return "";
      const type = this.reviewed.type.split("::").pop();
      if (type === "Percentage" && this.probing) {
        return "Measurement::Probing";
      }
      return `Measurement::${type}`;
    },
    measurementResults() {
      if (!this.reviewed.id) return "";
      const type = this.reviewed.type.split("::").pop();
      if (type === "Percentage") {
        if (this.probing) {
          return new Array(this.reviewed.probing_number_of_trial)
            .fill(null)
            .map((_i, idx) => {
              if (idx % 2 === 0) return true;
              else return false;
            });
        } else {
          // 0: true, 1: true, 2: true
          return new Array(this.reviewed.number_of_trial)
            .fill(null)
            .map((_i, idx) => {
              if (idx % 3 === 0) return true;
              else if (idx % 3 === 1) return false;
              else return null;
            });
        }
      }
      if (type === "Pir") {
        const count = Math.ceil(
          this.reviewed.duration / this.reviewed.interval
        );
        return new Array(count).fill(null);
      }
      if (type === "Duration") return { seconds: 3, string: "00:00:03" };
      if (type === "Frequency") return { score: 0 };
      if (type === "Prompting") {
        const results = {};
        this.reviewed.prompts.forEach((i, idx) => {
          results[i.id] = {
            ...i,
            score: idx,
            enabled: true,
            position: idx + 1
          };
        });
        return results;
      }
      if (type === "Sbt") {
        const results = {};
        const taskId =
          this.reviewed.target_tasks && this.reviewed.target_tasks.length > 0
            ? this.reviewed.target_tasks[0].id
            : 0;

        results["1"] = {
          key: "1",
          target_task_id: taskId,
          prompt_id: 0,
          target_problem_behavior_id: null
        };

        results["2"] = {
          key: "2",
          target_task_id:
            this.reviewed.target_tasks && this.reviewed.target_tasks.length > 1
              ? this.reviewed.target_tasks[1].id
              : taskId,
          prompt_id:
            this.reviewed.prompts && this.reviewed.prompts.length > 0
              ? this.reviewed.prompts[0].id
              : 0,
          target_problem_behavior_id: null
        };

        results["3"] = {
          key: "3",
          target_task_id:
            this.reviewed.target_tasks && this.reviewed.target_tasks.length > 0
              ? this.reviewed.target_tasks[0].id
              : 0,
          prompt_id:
            this.reviewed.prompts && this.reviewed.prompts.length > 1
              ? this.reviewed.prompts[1].id
              : taskId,
          target_problem_behavior_id:
            this.reviewed.target_problem_behaviors &&
            this.reviewed.target_problem_behaviors.length > 0
              ? this.reviewed.target_problem_behaviors[0].id
              : null
        };

        return results;
      }
      return {};
    }
  },
  watch: {
    selectedTargets(val) {
      if (val && val.length) {
        this.onSelectReview(val.at(0));
      } else {
        this.reviewed = { id: null };
      }
    },
    reviewed(val) {
      this.probing = false;
      this.generateGraphEntries(val);
      this.generateEntries(val);
    }
  },
  mounted() {
    if (this.selectedTargets && this.selectedTargets.length) {
      this.onSelectReview(this.selectedTargets.at(0));
    }
  },
  methods: {
    ...mapActions(["actionGet"]),
    async onSelectReview(target) {
      this.reviewLoading = true;
      const params = {
        url: `/api/v1/targets/${target.id}`
      };
      const { data } = await this.actionGet(params);
      this.reviewLoading = false;
      this.reviewed = data || { id: null };
    },
    getLabelDates(n, skipIndex = []) {
      return new Array(n).fill(null).map((_i, idx) => {
        if (skipIndex.includes(idx)) return null;
        else {
          return moment()
            .add(idx, "days")
            .format("YYYY-MM-DD");
        }
      });
    },
    generateGraphEntries(val) {
      this.graphEntries = { labels: [], datasets: [], recorded_by: [] };
      if (!val.id) return;
      // dummy datasets
      const percentageOrPir = {
        labels: this.getLabelDates(10),
        datasets: [
          { label: val.name, data: [30, 20, 50, 20, 80, 70, 50, 90, 100, 90] },
          { label: "Goal", data: new Array(10).fill(val.goal) }
        ],
        recorded_by: new Array(10).fill(this.currentUser.name)
      };
      const percentageProbing = {
        labels: this.getLabelDates(10, [3]),
        datasets: [
          {
            data: [20, 40, 30, null, null, null, null, null, null, null, null],
            color: "gold",
            label: "Probing"
          },
          {
            data: [null, null, null, null, 40, 50, 20, 60, 80, 90, 100],
            color: "lake",
            label: "Teaching"
          },
          { label: "Goal", data: new Array(10).fill(val.goal) }
        ],
        recorded_by: new Array(10).fill(this.currentUser.name)
      };
      const duration = {
        labels: this.getLabelDates(10),
        datasets: [
          { label: val.name, data: [3, 5, 2, 8, 10, 15, 12, 11, 15, 12] },
          { label: "Goal", data: new Array(10).fill(val.goal) }
        ],
        recorded_by: new Array(10).fill(this.currentUser.name)
      };
      const frequency = {
        labels: this.getLabelDates(10),
        datasets: [
          { label: val.name, data: [1, 3, 5, 2, 6, 9, 6, 4, 6, 8] },
          { label: "Goal", data: new Array(10).fill(val.goal) }
        ],
        recorded_by: new Array(10).fill(this.currentUser.name)
      };
      const prompting = {
        labels: this.getLabelDates(10),
        datasets: [
          {
            label: val.name,
            score: [97, 93, 82, 73, 66, 53, 48, 43, 40, 40],
            data: [
              {
                a: { score: 5, percentage: 71 },
                b: { score: 2, percentage: 29 },
                c: { score: 0, percentage: 0 },
                d: { score: 0, percentage: 0 },
                e: { score: 0, percentage: 0 },
                f: { score: 0, percentage: 0 },
                g: { score: 0, percentage: 0 },
                h: { score: 0, percentage: 0 },
                i: { score: 0, percentage: 0 },
                j: { score: 0, percentage: 0 },
                k: { score: 0, percentage: 0 },
                l: { score: 0, percentage: 0 },
                m: { score: 0, percentage: 0 },
                n: { score: 0, percentage: 0 },
                o: { score: 0, percentage: 0 },
                p: { score: 0, percentage: 0 },
                q: { score: 0, percentage: 0 },
                r: { score: 0, percentage: 0 },
                s: { score: 0, percentage: 0 },
                t: { score: 0, percentage: 0 },
                u: { score: 0, percentage: 0 },
                v: { score: 0, percentage: 0 },
                w: { score: 0, percentage: 0 },
                x: { score: 0, percentage: 0 },
                y: { score: 0, percentage: 0 },
                z: { score: 0, percentage: 0 }
              },
              {
                a: { score: 2, percentage: 33 },
                b: { score: 4, percentage: 67 },
                c: { score: 0, percentage: 0 },
                d: { score: 0, percentage: 0 },
                e: { score: 0, percentage: 0 },
                f: { score: 0, percentage: 0 },
                g: { score: 0, percentage: 0 },
                h: { score: 0, percentage: 0 },
                i: { score: 0, percentage: 0 },
                j: { score: 0, percentage: 0 },
                k: { score: 0, percentage: 0 },
                l: { score: 0, percentage: 0 },
                m: { score: 0, percentage: 0 },
                n: { score: 0, percentage: 0 },
                o: { score: 0, percentage: 0 },
                p: { score: 0, percentage: 0 },
                q: { score: 0, percentage: 0 },
                r: { score: 0, percentage: 0 },
                s: { score: 0, percentage: 0 },
                t: { score: 0, percentage: 0 },
                u: { score: 0, percentage: 0 },
                v: { score: 0, percentage: 0 },
                w: { score: 0, percentage: 0 },
                x: { score: 0, percentage: 0 },
                y: { score: 0, percentage: 0 },
                z: { score: 0, percentage: 0 }
              },
              {
                a: { score: 0, percentage: 0 },
                b: { score: 1, percentage: 17 },
                c: { score: 5, percentage: 83 },
                d: { score: 0, percentage: 0 },
                e: { score: 0, percentage: 0 },
                f: { score: 0, percentage: 0 },
                g: { score: 0, percentage: 0 },
                h: { score: 0, percentage: 0 },
                i: { score: 0, percentage: 0 },
                j: { score: 0, percentage: 0 },
                k: { score: 0, percentage: 0 },
                l: { score: 0, percentage: 0 },
                m: { score: 0, percentage: 0 },
                n: { score: 0, percentage: 0 },
                o: { score: 0, percentage: 0 },
                p: { score: 0, percentage: 0 },
                q: { score: 0, percentage: 0 },
                r: { score: 0, percentage: 0 },
                s: { score: 0, percentage: 0 },
                t: { score: 0, percentage: 0 },
                u: { score: 0, percentage: 0 },
                v: { score: 0, percentage: 0 },
                w: { score: 0, percentage: 0 },
                x: { score: 0, percentage: 0 },
                y: { score: 0, percentage: 0 },
                z: { score: 0, percentage: 0 }
              },
              {
                a: { score: 0, percentage: 0 },
                b: { score: 0, percentage: 0 },
                c: { score: 2, percentage: 29 },
                d: { score: 5, percentage: 71 },
                e: { score: 0, percentage: 0 },
                f: { score: 0, percentage: 0 },
                g: { score: 0, percentage: 0 },
                h: { score: 0, percentage: 0 },
                i: { score: 0, percentage: 0 },
                j: { score: 0, percentage: 0 },
                k: { score: 0, percentage: 0 },
                l: { score: 0, percentage: 0 },
                m: { score: 0, percentage: 0 },
                n: { score: 0, percentage: 0 },
                o: { score: 0, percentage: 0 },
                p: { score: 0, percentage: 0 },
                q: { score: 0, percentage: 0 },
                r: { score: 0, percentage: 0 },
                s: { score: 0, percentage: 0 },
                t: { score: 0, percentage: 0 },
                u: { score: 0, percentage: 0 },
                v: { score: 0, percentage: 0 },
                w: { score: 0, percentage: 0 },
                x: { score: 0, percentage: 0 },
                y: { score: 0, percentage: 0 },
                z: { score: 0, percentage: 0 }
              },
              {
                a: { score: 0, percentage: 0 },
                b: { score: 0, percentage: 0 },
                c: { score: 1, percentage: 14 },
                d: { score: 2, percentage: 29 },
                e: { score: 4, percentage: 57 },
                f: { score: 0, percentage: 0 },
                g: { score: 0, percentage: 0 },
                h: { score: 0, percentage: 0 },
                i: { score: 0, percentage: 0 },
                j: { score: 0, percentage: 0 },
                k: { score: 0, percentage: 0 },
                l: { score: 0, percentage: 0 },
                m: { score: 0, percentage: 0 },
                n: { score: 0, percentage: 0 },
                o: { score: 0, percentage: 0 },
                p: { score: 0, percentage: 0 },
                q: { score: 0, percentage: 0 },
                r: { score: 0, percentage: 0 },
                s: { score: 0, percentage: 0 },
                t: { score: 0, percentage: 0 },
                u: { score: 0, percentage: 0 },
                v: { score: 0, percentage: 0 },
                w: { score: 0, percentage: 0 },
                x: { score: 0, percentage: 0 },
                y: { score: 0, percentage: 0 },
                z: { score: 0, percentage: 0 }
              },
              {
                a: { score: 0, percentage: 0 },
                b: { score: 0, percentage: 0 },
                c: { score: 0, percentage: 0 },
                d: { score: 1, percentage: 10 },
                e: { score: 2, percentage: 20 },
                f: { score: 6, percentage: 60 },
                g: { score: 1, percentage: 10 },
                h: { score: 0, percentage: 0 },
                i: { score: 0, percentage: 0 },
                j: { score: 0, percentage: 0 },
                k: { score: 0, percentage: 0 },
                l: { score: 0, percentage: 0 },
                m: { score: 0, percentage: 0 },
                n: { score: 0, percentage: 0 },
                o: { score: 0, percentage: 0 },
                p: { score: 0, percentage: 0 },
                q: { score: 0, percentage: 0 },
                r: { score: 0, percentage: 0 },
                s: { score: 0, percentage: 0 },
                t: { score: 0, percentage: 0 },
                u: { score: 0, percentage: 0 },
                v: { score: 0, percentage: 0 },
                w: { score: 0, percentage: 0 },
                x: { score: 0, percentage: 0 },
                y: { score: 0, percentage: 0 },
                z: { score: 0, percentage: 0 }
              },
              {
                a: { score: 0, percentage: 0 },
                b: { score: 0, percentage: 0 },
                c: { score: 0, percentage: 0 },
                d: { score: 0, percentage: 0 },
                e: { score: 1, percentage: 13 },
                f: { score: 4, percentage: 50 },
                g: { score: 3, percentage: 38 },
                h: { score: 0, percentage: 0 },
                i: { score: 0, percentage: 0 },
                j: { score: 0, percentage: 0 },
                k: { score: 0, percentage: 0 },
                l: { score: 0, percentage: 0 },
                m: { score: 0, percentage: 0 },
                n: { score: 0, percentage: 0 },
                o: { score: 0, percentage: 0 },
                p: { score: 0, percentage: 0 },
                q: { score: 0, percentage: 0 },
                r: { score: 0, percentage: 0 },
                s: { score: 0, percentage: 0 },
                t: { score: 0, percentage: 0 },
                u: { score: 0, percentage: 0 },
                v: { score: 0, percentage: 0 },
                w: { score: 0, percentage: 0 },
                x: { score: 0, percentage: 0 },
                y: { score: 0, percentage: 0 },
                z: { score: 0, percentage: 0 }
              },
              {
                a: { score: 0, percentage: 0 },
                b: { score: 0, percentage: 0 },
                c: { score: 0, percentage: 0 },
                d: { score: 0, percentage: 0 },
                e: { score: 0, percentage: 0 },
                f: { score: 2, percentage: 25 },
                g: { score: 6, percentage: 75 },
                h: { score: 0, percentage: 0 },
                i: { score: 0, percentage: 0 },
                j: { score: 0, percentage: 0 },
                k: { score: 0, percentage: 0 },
                l: { score: 0, percentage: 0 },
                m: { score: 0, percentage: 0 },
                n: { score: 0, percentage: 0 },
                o: { score: 0, percentage: 0 },
                p: { score: 0, percentage: 0 },
                q: { score: 0, percentage: 0 },
                r: { score: 0, percentage: 0 },
                s: { score: 0, percentage: 0 },
                t: { score: 0, percentage: 0 },
                u: { score: 0, percentage: 0 },
                v: { score: 0, percentage: 0 },
                w: { score: 0, percentage: 0 },
                x: { score: 0, percentage: 0 },
                y: { score: 0, percentage: 0 },
                z: { score: 0, percentage: 0 }
              },
              {
                a: { score: 0, percentage: 0 },
                b: { score: 0, percentage: 0 },
                c: { score: 0, percentage: 0 },
                d: { score: 0, percentage: 0 },
                e: { score: 0, percentage: 0 },
                f: { score: 0, percentage: 0 },
                g: { score: 8, percentage: 100 },
                h: { score: 0, percentage: 0 },
                i: { score: 0, percentage: 0 },
                j: { score: 0, percentage: 0 },
                k: { score: 0, percentage: 0 },
                l: { score: 0, percentage: 0 },
                m: { score: 0, percentage: 0 },
                n: { score: 0, percentage: 0 },
                o: { score: 0, percentage: 0 },
                p: { score: 0, percentage: 0 },
                q: { score: 0, percentage: 0 },
                r: { score: 0, percentage: 0 },
                s: { score: 0, percentage: 0 },
                t: { score: 0, percentage: 0 },
                u: { score: 0, percentage: 0 },
                v: { score: 0, percentage: 0 },
                w: { score: 0, percentage: 0 },
                x: { score: 0, percentage: 0 },
                y: { score: 0, percentage: 0 },
                z: { score: 0, percentage: 0 }
              },
              {
                a: { score: 0, percentage: 0 },
                b: { score: 0, percentage: 0 },
                c: { score: 0, percentage: 0 },
                d: { score: 0, percentage: 0 },
                e: { score: 0, percentage: 0 },
                f: { score: 0, percentage: 0 },
                g: { score: 5, percentage: 100 },
                h: { score: 0, percentage: 0 },
                i: { score: 0, percentage: 0 },
                j: { score: 0, percentage: 0 },
                k: { score: 0, percentage: 0 },
                l: { score: 0, percentage: 0 },
                m: { score: 0, percentage: 0 },
                n: { score: 0, percentage: 0 },
                o: { score: 0, percentage: 0 },
                p: { score: 0, percentage: 0 },
                q: { score: 0, percentage: 0 },
                r: { score: 0, percentage: 0 },
                s: { score: 0, percentage: 0 },
                t: { score: 0, percentage: 0 },
                u: { score: 0, percentage: 0 },
                v: { score: 0, percentage: 0 },
                w: { score: 0, percentage: 0 },
                x: { score: 0, percentage: 0 },
                y: { score: 0, percentage: 0 },
                z: { score: 0, percentage: 0 }
              }
            ]
          },
          { label: "Goal", data: new Array(10).fill(val.goal) }
        ],
        recorded_by: new Array(10).fill(this.currentUser.name)
      };
      const sbt = {
        labels: this.getLabelDates(5),
        datasets: {
          label: val.name,
          data: {
            problem_behaviors: [
              {
                a: { score: 2 },
                b: { score: 4 },
                c: { score: 6 },
                d: { score: 8 },
                e: { score: 10 },
                f: { score: 0 },
                g: { score: 0 },
                h: { score: 0 },
                i: { score: 0 },
                j: { score: 0 },
                k: { score: 0 },
                l: { score: 0 },
                m: { score: 0 },
                n: { score: 0 },
                o: { score: 0 },
                p: { score: 0 },
                q: { score: 0 },
                r: { score: 0 },
                s: { score: 0 },
                t: { score: 0 },
                u: { score: 0 },
                v: { score: 0 },
                w: { score: 0 },
                x: { score: 0 },
                y: { score: 0 },
                z: { score: 0 }
              },
              {
                a: { score: 0 },
                b: { score: 1 },
                c: { score: 3 },
                d: { score: 5 },
                e: { score: 7 },
                f: { score: 9 },
                g: { score: 0 },
                h: { score: 0 },
                i: { score: 0 },
                j: { score: 0 },
                k: { score: 0 },
                l: { score: 0 },
                m: { score: 0 },
                n: { score: 0 },
                o: { score: 0 },
                p: { score: 0 },
                q: { score: 0 },
                r: { score: 0 },
                s: { score: 0 },
                t: { score: 0 },
                u: { score: 0 },
                v: { score: 0 },
                w: { score: 0 },
                x: { score: 0 },
                y: { score: 0 },
                z: { score: 0 }
              },
              {
                a: { score: 0 },
                b: { score: 0 },
                c: { score: 2 },
                d: { score: 4 },
                e: { score: 6 },
                f: { score: 8 },
                g: { score: 10 },
                h: { score: 0 },
                i: { score: 0 },
                j: { score: 0 },
                k: { score: 0 },
                l: { score: 0 },
                m: { score: 0 },
                n: { score: 0 },
                o: { score: 0 },
                p: { score: 0 },
                q: { score: 0 },
                r: { score: 0 },
                s: { score: 0 },
                t: { score: 0 },
                u: { score: 0 },
                v: { score: 0 },
                w: { score: 0 },
                x: { score: 0 },
                y: { score: 0 },
                z: { score: 0 }
              },
              {
                a: { score: 0 },
                b: { score: 0 },
                c: { score: 1 },
                d: { score: 3 },
                e: { score: 5 },
                f: { score: 7 },
                g: { score: 9 },
                h: { score: 0 },
                i: { score: 0 },
                j: { score: 0 },
                k: { score: 0 },
                l: { score: 0 },
                m: { score: 0 },
                n: { score: 0 },
                o: { score: 0 },
                p: { score: 0 },
                q: { score: 0 },
                r: { score: 0 },
                s: { score: 0 },
                t: { score: 0 },
                u: { score: 0 },
                v: { score: 0 },
                w: { score: 0 },
                x: { score: 0 },
                y: { score: 0 },
                z: { score: 0 }
              },
              {
                a: { score: 0 },
                b: { score: 0 },
                c: { score: 2 },
                d: { score: 4 },
                e: { score: 6 },
                f: { score: 8 },
                g: { score: 10 },
                h: { score: 0 },
                i: { score: 0 },
                j: { score: 0 },
                k: { score: 0 },
                l: { score: 0 },
                m: { score: 0 },
                n: { score: 0 },
                o: { score: 0 },
                p: { score: 0 },
                q: { score: 0 },
                r: { score: 0 },
                s: { score: 0 },
                t: { score: 0 },
                u: { score: 0 },
                v: { score: 0 },
                w: { score: 0 },
                x: { score: 0 },
                y: { score: 0 },
                z: { score: 0 }
              }
            ],
            tasks: [
              {
                a: { score: 100 },
                b: { score: 60 },
                c: { score: 20 },
                d: { score: 0 },
                e: { score: 0 },
                f: { score: 0 },
                g: { score: 0 },
                h: { score: 0 },
                i: { score: 0 },
                j: { score: 0 },
                k: { score: 0 },
                l: { score: 0 },
                m: { score: 0 },
                n: { score: 0 },
                o: { score: 0 },
                p: { score: 0 },
                q: { score: 0 },
                r: { score: 0 },
                s: { score: 0 },
                t: { score: 0 },
                u: { score: 0 },
                v: { score: 0 },
                w: { score: 0 },
                x: { score: 0 },
                y: { score: 0 },
                z: { score: 0 }
              },
              {
                a: { score: 0 },
                b: { score: 80 },
                c: { score: 60 },
                d: { score: 40 },
                e: { score: 0 },
                f: { score: 0 },
                g: { score: 0 },
                h: { score: 0 },
                i: { score: 0 },
                j: { score: 0 },
                k: { score: 0 },
                l: { score: 0 },
                m: { score: 0 },
                n: { score: 0 },
                o: { score: 0 },
                p: { score: 0 },
                q: { score: 0 },
                r: { score: 0 },
                s: { score: 0 },
                t: { score: 0 },
                u: { score: 0 },
                v: { score: 0 },
                w: { score: 0 },
                x: { score: 0 },
                y: { score: 0 },
                z: { score: 0 }
              },
              {
                a: { score: 0 },
                b: { score: 0 },
                c: { score: 70 },
                d: { score: 60 },
                e: { score: 50 },
                f: { score: 0 },
                g: { score: 0 },
                h: { score: 0 },
                i: { score: 0 },
                j: { score: 0 },
                k: { score: 0 },
                l: { score: 0 },
                m: { score: 0 },
                n: { score: 0 },
                o: { score: 0 },
                p: { score: 0 },
                q: { score: 0 },
                r: { score: 0 },
                s: { score: 0 },
                t: { score: 0 },
                u: { score: 0 },
                v: { score: 0 },
                w: { score: 0 },
                x: { score: 0 },
                y: { score: 0 },
                z: { score: 0 }
              },
              {
                a: { score: 0 },
                b: { score: 0 },
                c: { score: 0 },
                d: { score: 20 },
                e: { score: 40 },
                f: { score: 70 },
                g: { score: 0 },
                h: { score: 0 },
                i: { score: 0 },
                j: { score: 0 },
                k: { score: 0 },
                l: { score: 0 },
                m: { score: 0 },
                n: { score: 0 },
                o: { score: 0 },
                p: { score: 0 },
                q: { score: 0 },
                r: { score: 0 },
                s: { score: 0 },
                t: { score: 0 },
                u: { score: 0 },
                v: { score: 0 },
                w: { score: 0 },
                x: { score: 0 },
                y: { score: 0 },
                z: { score: 0 }
              },
              {
                a: { score: 50 },
                b: { score: 70 },
                c: { score: 10 },
                d: { score: 0 },
                e: { score: 0 },
                f: { score: 0 },
                g: { score: 0 },
                h: { score: 0 },
                i: { score: 0 },
                j: { score: 0 },
                k: { score: 0 },
                l: { score: 0 },
                m: { score: 0 },
                n: { score: 0 },
                o: { score: 0 },
                p: { score: 0 },
                q: { score: 0 },
                r: { score: 0 },
                s: { score: 0 },
                t: { score: 0 },
                u: { score: 0 },
                v: { score: 0 },
                w: { score: 0 },
                x: { score: 0 },
                y: { score: 0 },
                z: { score: 0 }
              }
            ]
          }
        },
        recorded_by: new Array(10).fill(this.currentUser.name)
      };
      // setup current dataset
      if (val.type === "Target::Percentage") {
        if (val.probing_enable) this.graphEntries = percentageProbing;
        else this.graphEntries = percentageOrPir;
      }
      if (val.type === "Target::Pir") this.graphEntries = percentageOrPir;
      if (val.type === "Target::Duration") this.graphEntries = duration;
      if (val.type === "Target::Frequency") this.graphEntries = frequency;
      if (val.type === "Target::Prompting") {
        const prompts = val.prompts.map(i => i.id);
        prompting.datasets[0].data = prompting.datasets[0].data.map(dataset => {
          const data = {};
          Object.keys(dataset).forEach((key, idx) => {
            const temp = dataset[key] || { score: 0, percentage: 0 };
            data[prompts[idx]] = {
              frequency: temp.score,
              percentage: temp.percentage
            };
          });
          return data;
        });
        this.graphEntries = prompting;
      }
      if (val.type === "Target::Sbt") {
        const taskIds = val.target_tasks.map(i => i.id);
        const problemBehaviorIds = val.target_problem_behaviors.map(i => i.id);
        sbt.datasets.data.tasks = sbt.datasets.data.tasks.map(dataset => {
          const data = {};
          Object.keys(dataset).forEach((key, idx) => {
            const temp = dataset[key] || { score: 0 };
            if (taskIds[idx]) {
              data[taskIds[idx]] = temp;
            }
          });
          return data;
        });
        sbt.datasets.data.problem_behaviors = sbt.datasets.data.problem_behaviors.map(
          dataset => {
            const data = {};
            Object.keys(dataset).forEach((key, idx) => {
              const temp = dataset[key] || { score: 0 };
              if (problemBehaviorIds[idx]) {
                data[problemBehaviorIds[idx]] = temp;
              }
            });
            return data;
          }
        );
        this.graphEntries = sbt;
      }
    },
    generateEntries(val) {
      this.entries = [];
      if (!val.id) return;
      // dummy datasets
      const percentage = [
        { score: 30, type: "Entry::Percentage" },
        { score: 20, type: "Entry::Percentage" },
        { score: 50, type: "Entry::Percentage" },
        { score: 20, type: "Entry::Percentage" },
        { score: 80, type: "Entry::Percentage" },
        { score: 70, type: "Entry::Percentage" },
        { score: 50, type: "Entry::Percentage" },
        { score: 90, type: "Entry::Percentage" },
        { score: 100, type: "Entry::Percentage" },
        { score: 90, type: "Entry::Percentage" }
      ];
      const percentageProbing = [
        {
          type: "Entry::PhaseLine",
          line_label: "Probing",
          line_color: "gold",
          line_description: null,
          position: 1,
          is_baseline: true,
          is_probing: true
        },
        { score: 20, type: "Entry::Probing" },
        { score: 40, type: "Entry::Probing" },
        { score: 30, type: "Entry::Probing" },
        {
          type: "Entry::PhaseLine",
          line_label: "Teaching",
          line_color: "lake",
          line_description: null,
          is_baseline: false,
          is_probing: false
        },
        { score: 40, type: "Entry::Percentage" },
        { score: 50, type: "Entry::Percentage" },
        { score: 20, type: "Entry::Percentage" },
        { score: 60, type: "Entry::Percentage" },
        { score: 80, type: "Entry::Percentage" },
        { score: 90, type: "Entry::Percentage" },
        { score: 100, type: "Entry::Percentage" }
      ];
      const pir = [
        { score: 30, percentage: 30, type: "Entry::Pir" },
        { score: 20, percentage: 20, type: "Entry::Pir" },
        { score: 50, percentage: 50, type: "Entry::Pir" },
        { score: 20, percentage: 20, type: "Entry::Pir" },
        { score: 80, percentage: 80, type: "Entry::Pir" },
        { score: 70, percentage: 70, type: "Entry::Pir" },
        { score: 50, percentage: 50, type: "Entry::Pir" },
        { score: 90, percentage: 90, type: "Entry::Pir" },
        { score: 100, percentage: 100, type: "Entry::Pir" },
        { score: 90, percentage: 90, type: "Entry::Pir" }
      ];
      const duration = [
        { recorded_time: "00:00:03", type: "Entry::Duration" },
        { recorded_time: "00:00:05", type: "Entry::Duration" },
        { recorded_time: "00:00:02", type: "Entry::Duration" },
        { recorded_time: "00:00:08", type: "Entry::Duration" },
        { recorded_time: "00:00:10", type: "Entry::Duration" },
        { recorded_time: "00:00:15", type: "Entry::Duration" },
        { recorded_time: "00:00:12", type: "Entry::Duration" },
        { recorded_time: "00:00:11", type: "Entry::Duration" },
        { recorded_time: "00:00:15", type: "Entry::Duration" },
        { recorded_time: "00:00:12", type: "Entry::Duration" }
      ];
      const frequency = [
        { score: 1, type: "Entry::Frequency" },
        { score: 3, type: "Entry::Frequency" },
        { score: 5, type: "Entry::Frequency" },
        { score: 2, type: "Entry::Frequency" },
        { score: 6, type: "Entry::Frequency" },
        { score: 9, type: "Entry::Frequency" },
        { score: 6, type: "Entry::Frequency" },
        { score: 4, type: "Entry::Frequency" },
        { score: 6, type: "Entry::Frequency" },
        { score: 8, type: "Entry::Frequency" }
      ];
      const prompting = [
        {
          score: (100 * 5 + 90 * 2) / 7,
          scores: {
            a: { score: 5, percentage: 71 },
            b: { score: 2, percentage: 29 },
            c: { score: 0, percentage: 0 },
            d: { score: 0, percentage: 0 },
            e: { score: 0, percentage: 0 },
            f: { score: 0, percentage: 0 },
            g: { score: 0, percentage: 0 },
            h: { score: 0, percentage: 0 },
            i: { score: 0, percentage: 0 },
            j: { score: 0, percentage: 0 },
            k: { score: 0, percentage: 0 },
            l: { score: 0, percentage: 0 },
            m: { score: 0, percentage: 0 },
            n: { score: 0, percentage: 0 },
            o: { score: 0, percentage: 0 },
            p: { score: 0, percentage: 0 },
            q: { score: 0, percentage: 0 },
            r: { score: 0, percentage: 0 },
            s: { score: 0, percentage: 0 },
            t: { score: 0, percentage: 0 },
            u: { score: 0, percentage: 0 },
            v: { score: 0, percentage: 0 },
            w: { score: 0, percentage: 0 },
            x: { score: 0, percentage: 0 },
            y: { score: 0, percentage: 0 },
            z: { score: 0, percentage: 0 }
          },
          type: "Entry::Prompting"
        },
        {
          score: (100 * 2 + 90 * 4) / 6,
          scores: {
            a: { score: 2, percentage: 33 },
            b: { score: 4, percentage: 67 },
            c: { score: 0, percentage: 0 },
            d: { score: 0, percentage: 0 },
            e: { score: 0, percentage: 0 },
            f: { score: 0, percentage: 0 },
            g: { score: 0, percentage: 0 },
            h: { score: 0, percentage: 0 },
            i: { score: 0, percentage: 0 },
            j: { score: 0, percentage: 0 },
            k: { score: 0, percentage: 0 },
            l: { score: 0, percentage: 0 },
            m: { score: 0, percentage: 0 },
            n: { score: 0, percentage: 0 },
            o: { score: 0, percentage: 0 },
            p: { score: 0, percentage: 0 },
            q: { score: 0, percentage: 0 },
            r: { score: 0, percentage: 0 },
            s: { score: 0, percentage: 0 },
            t: { score: 0, percentage: 0 },
            u: { score: 0, percentage: 0 },
            v: { score: 0, percentage: 0 },
            w: { score: 0, percentage: 0 },
            x: { score: 0, percentage: 0 },
            y: { score: 0, percentage: 0 },
            z: { score: 0, percentage: 0 }
          },
          type: "Entry::Prompting"
        },
        {
          score: (90 * 1 + 80 * 5) / 6,
          scores: {
            a: { score: 0, percentage: 0 },
            b: { score: 1, percentage: 17 },
            c: { score: 5, percentage: 83 },
            d: { score: 0, percentage: 0 },
            e: { score: 0, percentage: 0 },
            f: { score: 0, percentage: 0 },
            g: { score: 0, percentage: 0 },
            h: { score: 0, percentage: 0 },
            i: { score: 0, percentage: 0 },
            j: { score: 0, percentage: 0 },
            k: { score: 0, percentage: 0 },
            l: { score: 0, percentage: 0 },
            m: { score: 0, percentage: 0 },
            n: { score: 0, percentage: 0 },
            o: { score: 0, percentage: 0 },
            p: { score: 0, percentage: 0 },
            q: { score: 0, percentage: 0 },
            r: { score: 0, percentage: 0 },
            s: { score: 0, percentage: 0 },
            t: { score: 0, percentage: 0 },
            u: { score: 0, percentage: 0 },
            v: { score: 0, percentage: 0 },
            w: { score: 0, percentage: 0 },
            x: { score: 0, percentage: 0 },
            y: { score: 0, percentage: 0 },
            z: { score: 0, percentage: 0 }
          },
          type: "Entry::Prompting"
        },
        {
          score: (80 * 2 + 70 * 5) / 7,
          scores: {
            a: { score: 0, percentage: 0 },
            b: { score: 0, percentage: 0 },
            c: { score: 2, percentage: 29 },
            d: { score: 5, percentage: 71 },
            e: { score: 0, percentage: 0 },
            f: { score: 0, percentage: 0 },
            g: { score: 0, percentage: 0 },
            h: { score: 0, percentage: 0 },
            i: { score: 0, percentage: 0 },
            j: { score: 0, percentage: 0 },
            k: { score: 0, percentage: 0 },
            l: { score: 0, percentage: 0 },
            m: { score: 0, percentage: 0 },
            n: { score: 0, percentage: 0 },
            o: { score: 0, percentage: 0 },
            p: { score: 0, percentage: 0 },
            q: { score: 0, percentage: 0 },
            r: { score: 0, percentage: 0 },
            s: { score: 0, percentage: 0 },
            t: { score: 0, percentage: 0 },
            u: { score: 0, percentage: 0 },
            v: { score: 0, percentage: 0 },
            w: { score: 0, percentage: 0 },
            x: { score: 0, percentage: 0 },
            y: { score: 0, percentage: 0 },
            z: { score: 0, percentage: 0 }
          },
          type: "Entry::Prompting"
        },
        {
          score: (80 * 1 + 70 * 2 + 60 * 4) / 7,
          scores: {
            a: { score: 0, percentage: 0 },
            b: { score: 0, percentage: 0 },
            c: { score: 1, percentage: 14 },
            d: { score: 2, percentage: 29 },
            e: { score: 4, percentage: 57 },
            f: { score: 0, percentage: 0 },
            g: { score: 0, percentage: 0 },
            h: { score: 0, percentage: 0 },
            i: { score: 0, percentage: 0 },
            j: { score: 0, percentage: 0 },
            k: { score: 0, percentage: 0 },
            l: { score: 0, percentage: 0 },
            m: { score: 0, percentage: 0 },
            n: { score: 0, percentage: 0 },
            o: { score: 0, percentage: 0 },
            p: { score: 0, percentage: 0 },
            q: { score: 0, percentage: 0 },
            r: { score: 0, percentage: 0 },
            s: { score: 0, percentage: 0 },
            t: { score: 0, percentage: 0 },
            u: { score: 0, percentage: 0 },
            v: { score: 0, percentage: 0 },
            w: { score: 0, percentage: 0 },
            x: { score: 0, percentage: 0 },
            y: { score: 0, percentage: 0 },
            z: { score: 0, percentage: 0 }
          },
          type: "Entry::Prompting"
        },
        {
          score: (70 * 1 + 60 * 2 + 50 * 6 + 40 * 1) / 10,
          scores: {
            a: { score: 0, percentage: 0 },
            b: { score: 0, percentage: 0 },
            c: { score: 0, percentage: 0 },
            d: { score: 1, percentage: 10 },
            e: { score: 2, percentage: 20 },
            f: { score: 6, percentage: 60 },
            g: { score: 1, percentage: 10 },
            h: { score: 0, percentage: 0 },
            i: { score: 0, percentage: 0 },
            j: { score: 0, percentage: 0 },
            k: { score: 0, percentage: 0 },
            l: { score: 0, percentage: 0 },
            m: { score: 0, percentage: 0 },
            n: { score: 0, percentage: 0 },
            o: { score: 0, percentage: 0 },
            p: { score: 0, percentage: 0 },
            q: { score: 0, percentage: 0 },
            r: { score: 0, percentage: 0 },
            s: { score: 0, percentage: 0 },
            t: { score: 0, percentage: 0 },
            u: { score: 0, percentage: 0 },
            v: { score: 0, percentage: 0 },
            w: { score: 0, percentage: 0 },
            x: { score: 0, percentage: 0 },
            y: { score: 0, percentage: 0 },
            z: { score: 0, percentage: 0 }
          },
          type: "Entry::Prompting"
        },
        {
          score: (60 * 1 + 50 * 4 + 40 * 3) / 8,
          scores: {
            a: { score: 0, percentage: 0 },
            b: { score: 0, percentage: 0 },
            c: { score: 0, percentage: 0 },
            d: { score: 0, percentage: 0 },
            e: { score: 1, percentage: 13 },
            f: { score: 4, percentage: 50 },
            g: { score: 3, percentage: 38 },
            h: { score: 0, percentage: 0 },
            i: { score: 0, percentage: 0 },
            j: { score: 0, percentage: 0 },
            k: { score: 0, percentage: 0 },
            l: { score: 0, percentage: 0 },
            m: { score: 0, percentage: 0 },
            n: { score: 0, percentage: 0 },
            o: { score: 0, percentage: 0 },
            p: { score: 0, percentage: 0 },
            q: { score: 0, percentage: 0 },
            r: { score: 0, percentage: 0 },
            s: { score: 0, percentage: 0 },
            t: { score: 0, percentage: 0 },
            u: { score: 0, percentage: 0 },
            v: { score: 0, percentage: 0 },
            w: { score: 0, percentage: 0 },
            x: { score: 0, percentage: 0 },
            y: { score: 0, percentage: 0 },
            z: { score: 0, percentage: 0 }
          },
          type: "Entry::Prompting"
        },
        {
          score: (50 * 2 + 40 * 6) / 8,
          scores: {
            a: { score: 0, percentage: 0 },
            b: { score: 0, percentage: 0 },
            c: { score: 0, percentage: 0 },
            d: { score: 0, percentage: 0 },
            e: { score: 0, percentage: 0 },
            f: { score: 2, percentage: 25 },
            g: { score: 6, percentage: 75 },
            h: { score: 0, percentage: 0 },
            i: { score: 0, percentage: 0 },
            j: { score: 0, percentage: 0 },
            k: { score: 0, percentage: 0 },
            l: { score: 0, percentage: 0 },
            m: { score: 0, percentage: 0 },
            n: { score: 0, percentage: 0 },
            o: { score: 0, percentage: 0 },
            p: { score: 0, percentage: 0 },
            q: { score: 0, percentage: 0 },
            r: { score: 0, percentage: 0 },
            s: { score: 0, percentage: 0 },
            t: { score: 0, percentage: 0 },
            u: { score: 0, percentage: 0 },
            v: { score: 0, percentage: 0 },
            w: { score: 0, percentage: 0 },
            x: { score: 0, percentage: 0 },
            y: { score: 0, percentage: 0 },
            z: { score: 0, percentage: 0 }
          },
          type: "Entry::Prompting"
        },
        {
          score: (40 * 8) / 8,
          scores: {
            a: { score: 0, percentage: 0 },
            b: { score: 0, percentage: 0 },
            c: { score: 0, percentage: 0 },
            d: { score: 0, percentage: 0 },
            e: { score: 0, percentage: 0 },
            f: { score: 0, percentage: 0 },
            g: { score: 8, percentage: 100 },
            h: { score: 0, percentage: 0 },
            i: { score: 0, percentage: 0 },
            j: { score: 0, percentage: 0 },
            k: { score: 0, percentage: 0 },
            l: { score: 0, percentage: 0 },
            m: { score: 0, percentage: 0 },
            n: { score: 0, percentage: 0 },
            o: { score: 0, percentage: 0 },
            p: { score: 0, percentage: 0 },
            q: { score: 0, percentage: 0 },
            r: { score: 0, percentage: 0 },
            s: { score: 0, percentage: 0 },
            t: { score: 0, percentage: 0 },
            u: { score: 0, percentage: 0 },
            v: { score: 0, percentage: 0 },
            w: { score: 0, percentage: 0 },
            x: { score: 0, percentage: 0 },
            y: { score: 0, percentage: 0 },
            z: { score: 0, percentage: 0 }
          },
          type: "Entry::Prompting"
        },
        {
          score: (40 * 5) / 5,
          scores: {
            a: { score: 0, percentage: 0 },
            b: { score: 0, percentage: 0 },
            c: { score: 0, percentage: 0 },
            d: { score: 0, percentage: 0 },
            e: { score: 0, percentage: 0 },
            f: { score: 0, percentage: 0 },
            g: { score: 5, percentage: 100 },
            h: { score: 0, percentage: 0 },
            i: { score: 0, percentage: 0 },
            j: { score: 0, percentage: 0 },
            k: { score: 0, percentage: 0 },
            l: { score: 0, percentage: 0 },
            m: { score: 0, percentage: 0 },
            n: { score: 0, percentage: 0 },
            o: { score: 0, percentage: 0 },
            p: { score: 0, percentage: 0 },
            q: { score: 0, percentage: 0 },
            r: { score: 0, percentage: 0 },
            s: { score: 0, percentage: 0 },
            t: { score: 0, percentage: 0 },
            u: { score: 0, percentage: 0 },
            v: { score: 0, percentage: 0 },
            w: { score: 0, percentage: 0 },
            x: { score: 0, percentage: 0 },
            y: { score: 0, percentage: 0 },
            z: { score: 0, percentage: 0 }
          },
          type: "Entry::Prompting"
        }
      ];
      const sbtEntries = [
        {
          scores: {
            a: {
              key: 1,
              prompt_score: 100,
              behavior_score: 2
            },
            b: {
              key: 2,
              prompt_score: 60,
              behavior_score: 4
            },
            c: {
              key: 3,
              prompt_score: 20,
              behavior_score: 6
            },
            d: {
              key: 4,
              prompt_score: 0,
              behavior_score: 8
            },
            e: {
              key: 5,
              prompt_score: 0,
              behavior_score: 10
            },
            f: {
              key: 6,
              prompt_score: 0,
              behavior_score: 0
            },
            g: {
              key: 7,
              prompt_score: 0,
              behavior_score: 0
            },
            h: {
              key: 8,
              prompt_score: 0,
              behavior_score: 0
            },
            i: {
              key: 9,
              prompt_score: 0,
              behavior_score: 0
            },
            j: {
              key: 10,
              prompt_score: 0,
              behavior_score: 0
            },
            k: {
              key: 11,
              prompt_score: 0,
              behavior_score: 0
            },
            l: {
              key: 12,
              prompt_score: 0,
              behavior_score: 0
            },
            m: {
              key: 13,
              prompt_score: 0,
              behavior_score: 0
            },
            n: {
              key: 14,
              prompt_score: 0,
              behavior_score: 0
            },
            o: {
              key: 15,
              prompt_score: 0,
              behavior_score: 0
            },
            p: {
              key: 16,
              prompt_score: 0,
              behavior_score: 0
            },
            q: {
              key: 17,
              prompt_score: 0,
              behavior_score: 0
            },
            r: {
              key: 18,
              prompt_score: 0,
              behavior_score: 0
            },
            s: {
              key: 19,
              prompt_score: 0,
              behavior_score: 0
            },
            t: {
              key: 20,
              prompt_score: 0,
              behavior_score: 0
            },
            u: {
              key: 21,
              prompt_score: 0,
              behavior_score: 0
            },
            v: {
              key: 22,
              prompt_score: 0,
              behavior_score: 0
            },
            w: {
              key: 23,
              prompt_score: 0,
              behavior_score: 0
            },
            x: {
              key: 24,
              prompt_score: 0,
              behavior_score: 0
            },
            y: {
              key: 25,
              prompt_score: 0,
              behavior_score: 0
            },
            z: {
              key: 26,
              prompt_score: 0,
              behavior_score: 0
            }
          },
          type: "Entry::Sbt"
        },
        {
          scores: {
            a: {
              key: 1,
              prompt_score: 0,
              behavior_score: 0
            },
            b: {
              key: 2,
              prompt_score: 80,
              behavior_score: 1
            },
            c: {
              key: 3,
              prompt_score: 60,
              behavior_score: 3
            },
            d: {
              key: 4,
              prompt_score: 4,
              behavior_score: 5
            },
            e: {
              key: 5,
              prompt_score: 0,
              behavior_score: 7
            },
            f: {
              key: 6,
              prompt_score: 0,
              behavior_score: 0
            },
            g: {
              key: 7,
              prompt_score: 0,
              behavior_score: 0
            },
            h: {
              key: 8,
              prompt_score: 0,
              behavior_score: 0
            },
            i: {
              key: 9,
              prompt_score: 0,
              behavior_score: 0
            },
            j: {
              key: 10,
              prompt_score: 0,
              behavior_score: 0
            },
            k: {
              key: 11,
              prompt_score: 0,
              behavior_score: 0
            },
            l: {
              key: 12,
              prompt_score: 0,
              behavior_score: 0
            },
            m: {
              key: 13,
              prompt_score: 0,
              behavior_score: 0
            },
            n: {
              key: 14,
              prompt_score: 0,
              behavior_score: 0
            },
            o: {
              key: 15,
              prompt_score: 0,
              behavior_score: 0
            },
            p: {
              key: 16,
              prompt_score: 0,
              behavior_score: 0
            },
            q: {
              key: 17,
              prompt_score: 0,
              behavior_score: 0
            },
            r: {
              key: 18,
              prompt_score: 0,
              behavior_score: 0
            },
            s: {
              key: 19,
              prompt_score: 0,
              behavior_score: 0
            },
            t: {
              key: 20,
              prompt_score: 0,
              behavior_score: 0
            },
            u: {
              key: 21,
              prompt_score: 0,
              behavior_score: 0
            },
            v: {
              key: 22,
              prompt_score: 0,
              behavior_score: 0
            },
            w: {
              key: 23,
              prompt_score: 0,
              behavior_score: 0
            },
            x: {
              key: 24,
              prompt_score: 0,
              behavior_score: 0
            },
            y: {
              key: 25,
              prompt_score: 0,
              behavior_score: 0
            },
            z: {
              key: 26,
              prompt_score: 0,
              behavior_score: 0
            }
          },
          type: "Entry::Sbt"
        },
        {
          scores: {
            a: {
              key: 1,
              prompt_score: 0,
              behavior_score: 0
            },
            b: {
              key: 2,
              prompt_score: 0,
              behavior_score: 2
            },
            c: {
              key: 3,
              prompt_score: 70,
              behavior_score: 4
            },
            d: {
              key: 4,
              prompt_score: 60,
              behavior_score: 6
            },
            e: {
              key: 5,
              prompt_score: 50,
              behavior_score: 0
            },
            f: {
              key: 6,
              prompt_score: 0,
              behavior_score: 0
            },
            g: {
              key: 7,
              prompt_score: 0,
              behavior_score: 0
            },
            h: {
              key: 8,
              prompt_score: 0,
              behavior_score: 0
            },
            i: {
              key: 9,
              prompt_score: 0,
              behavior_score: 0
            },
            j: {
              key: 10,
              prompt_score: 0,
              behavior_score: 0
            },
            k: {
              key: 11,
              prompt_score: 0,
              behavior_score: 0
            },
            l: {
              key: 12,
              prompt_score: 0,
              behavior_score: 0
            },
            m: {
              key: 13,
              prompt_score: 0,
              behavior_score: 0
            },
            n: {
              key: 14,
              prompt_score: 0,
              behavior_score: 0
            },
            o: {
              key: 15,
              prompt_score: 0,
              behavior_score: 0
            },
            p: {
              key: 16,
              prompt_score: 0,
              behavior_score: 0
            },
            q: {
              key: 17,
              prompt_score: 0,
              behavior_score: 0
            },
            r: {
              key: 18,
              prompt_score: 0,
              behavior_score: 0
            },
            s: {
              key: 19,
              prompt_score: 0,
              behavior_score: 0
            },
            t: {
              key: 20,
              prompt_score: 0,
              behavior_score: 0
            },
            u: {
              key: 21,
              prompt_score: 0,
              behavior_score: 0
            },
            v: {
              key: 22,
              prompt_score: 0,
              behavior_score: 0
            },
            w: {
              key: 23,
              prompt_score: 0,
              behavior_score: 0
            },
            x: {
              key: 24,
              prompt_score: 0,
              behavior_score: 0
            },
            y: {
              key: 25,
              prompt_score: 0,
              behavior_score: 0
            },
            z: {
              key: 26,
              prompt_score: 0,
              behavior_score: 0
            }
          },
          type: "Entry::Sbt"
        },
        {
          scores: {
            a: {
              key: 1,
              prompt_score: 0,
              behavior_score: 0
            },
            b: {
              key: 2,
              prompt_score: 0,
              behavior_score: 0
            },
            c: {
              key: 3,
              prompt_score: 0,
              behavior_score: 1
            },
            d: {
              key: 4,
              prompt_score: 2,
              behavior_score: 3
            },
            e: {
              key: 5,
              prompt_score: 4,
              behavior_score: 5
            },
            f: {
              key: 6,
              prompt_score: 0,
              behavior_score: 0
            },
            g: {
              key: 7,
              prompt_score: 0,
              behavior_score: 0
            },
            h: {
              key: 8,
              prompt_score: 0,
              behavior_score: 0
            },
            i: {
              key: 9,
              prompt_score: 0,
              behavior_score: 0
            },
            j: {
              key: 10,
              prompt_score: 0,
              behavior_score: 0
            },
            k: {
              key: 11,
              prompt_score: 0,
              behavior_score: 0
            },
            l: {
              key: 12,
              prompt_score: 0,
              behavior_score: 0
            },
            m: {
              key: 13,
              prompt_score: 0,
              behavior_score: 0
            },
            n: {
              key: 14,
              prompt_score: 0,
              behavior_score: 0
            },
            o: {
              key: 15,
              prompt_score: 0,
              behavior_score: 0
            },
            p: {
              key: 16,
              prompt_score: 0,
              behavior_score: 0
            },
            q: {
              key: 17,
              prompt_score: 0,
              behavior_score: 0
            },
            r: {
              key: 18,
              prompt_score: 0,
              behavior_score: 0
            },
            s: {
              key: 19,
              prompt_score: 0,
              behavior_score: 0
            },
            t: {
              key: 20,
              prompt_score: 0,
              behavior_score: 0
            },
            u: {
              key: 21,
              prompt_score: 0,
              behavior_score: 0
            },
            v: {
              key: 22,
              prompt_score: 0,
              behavior_score: 0
            },
            w: {
              key: 23,
              prompt_score: 0,
              behavior_score: 0
            },
            x: {
              key: 24,
              prompt_score: 0,
              behavior_score: 0
            },
            y: {
              key: 25,
              prompt_score: 0,
              behavior_score: 0
            },
            z: {
              key: 26,
              prompt_score: 0,
              behavior_score: 0
            }
          },
          type: "Entry::Sbt"
        },
        {
          scores: {
            a: {
              key: 1,
              prompt_score: 50,
              behavior_score: 0
            },
            b: {
              key: 2,
              prompt_score: 70,
              behavior_score: 0
            },
            c: {
              key: 3,
              prompt_score: 10,
              behavior_score: 2
            },
            d: {
              key: 4,
              prompt_score: 0,
              behavior_score: 4
            },
            e: {
              key: 5,
              prompt_score: 0,
              behavior_score: 6
            },
            f: {
              key: 6,
              prompt_score: 0,
              behavior_score: 0
            },
            g: {
              key: 7,
              prompt_score: 0,
              behavior_score: 0
            },
            h: {
              key: 8,
              prompt_score: 0,
              behavior_score: 0
            },
            i: {
              key: 9,
              prompt_score: 0,
              behavior_score: 0
            },
            j: {
              key: 10,
              prompt_score: 0,
              behavior_score: 0
            },
            k: {
              key: 11,
              prompt_score: 0,
              behavior_score: 0
            },
            l: {
              key: 12,
              prompt_score: 0,
              behavior_score: 0
            },
            m: {
              key: 13,
              prompt_score: 0,
              behavior_score: 0
            },
            n: {
              key: 14,
              prompt_score: 0,
              behavior_score: 0
            },
            o: {
              key: 15,
              prompt_score: 0,
              behavior_score: 0
            },
            p: {
              key: 16,
              prompt_score: 0,
              behavior_score: 0
            },
            q: {
              key: 17,
              prompt_score: 0,
              behavior_score: 0
            },
            r: {
              key: 18,
              prompt_score: 0,
              behavior_score: 0
            },
            s: {
              key: 19,
              prompt_score: 0,
              behavior_score: 0
            },
            t: {
              key: 20,
              prompt_score: 0,
              behavior_score: 0
            },
            u: {
              key: 21,
              prompt_score: 0,
              behavior_score: 0
            },
            v: {
              key: 22,
              prompt_score: 0,
              behavior_score: 0
            },
            w: {
              key: 23,
              prompt_score: 0,
              behavior_score: 0
            },
            x: {
              key: 24,
              prompt_score: 0,
              behavior_score: 0
            },
            y: {
              key: 25,
              prompt_score: 0,
              behavior_score: 0
            },
            z: {
              key: 26,
              prompt_score: 0,
              behavior_score: 0
            }
          },
          type: "Entry::Sbt"
        }
      ];
      // setup current dataset
      let current = [];
      if (val.type === "Target::Percentage") {
        if (val.probing_enable) current = percentageProbing;
        else current = percentage;
      }
      if (val.type === "Target::Pir") current = pir;
      if (val.type === "Target::Duration") current = duration;
      if (val.type === "Target::Frequency") current = frequency;
      if (val.type === "Target::Prompting") {
        const prompts = val.prompts.map(i => i.id);
        current = prompting.map(data => {
          const scores = {};
          Object.keys(data.scores).forEach((key, idx) => {
            const temp = data.scores[key] || { score: 0, percentage: 0 };
            scores[prompts[idx]] = temp;
          });
          return { score: Math.round(data.score), scores, type: data.type };
        });
      }
      if (val.type === "Target::Sbt") {
        const taskIds = val.target_tasks.map(i => i.id);
        const problemBehaviorIds = val.target_problem_behaviors.map(i => i.id);
        current = sbtEntries.map(data => {
          const results = {};
          let entryCounter = 1;

          Object.keys(data.scores).forEach((key, idx) => {
            const temp = data.scores[key] || { prompt_score: 0 };
            const behaviorScore =
              temp.behavior_score === 0 ? 1 : temp.behavior_score;

            const hasProblemBehavior =
              temp.behavior_score !== 0 && temp.behavior_score > 0;

            if (taskIds[idx]) {
              for (
                let i = 0;
                i < (behaviorScore > 0 ? behaviorScore : 1);
                i++
              ) {
                results[entryCounter] = {
                  ...temp,
                  key: entryCounter,
                  target_task_id: taskIds[idx],
                  task_code: val.target_tasks[idx].code,
                  task_color: val.target_tasks[idx].color,
                  prompt_id: null,
                  prompt_code: null,
                  problem_behavior_code:
                    hasProblemBehavior && problemBehaviorIds[idx]
                      ? val.target_problem_behaviors[idx].code
                      : null,
                  problem_behavior_id:
                    hasProblemBehavior && problemBehaviorIds[idx]
                      ? val.target_problem_behaviors[idx].problem_behavior_id
                      : null,
                  target_problem_behavior_id:
                    hasProblemBehavior && problemBehaviorIds[idx]
                      ? val.target_problem_behaviors[idx].id
                      : null
                };
                entryCounter++;
              }
            }
          });

          return {
            scores: results,
            type: data.type
          };
        });
      }
      this.entries = current.map((i, idx) => {
        return {
          ...i,
          id: idx + 1,
          recorded_at: moment()
            .add(idx, "days")
            .format(),
          comment:
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
          target_type: val.type,
          position: idx + 1,
          session: {
            user: this.currentUser
          }
        };
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/target/modal/ReviewTargetDatabank.vue